import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Box, Typography, Select, MenuItem } from "@mui/material";
import PropTypes from "prop-types";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import ClaimStatusBadge from "../ClaimStatusBadge.jsx";
import ViewAllLink from "../ViewAllLink.jsx";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import { useSnackbar } from "notistack";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import {
  colorsFoundation,
  dimensions,
  typographyTokens,
} from "@accredo/fe-shared/theme/index.js";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";

import { useClaimStates } from "../../hooks/useClaimStates.js";
import { applySortingWithAccessorKeys } from "./utils/tableUtils.js";

/**
 * ClaimsTable Component
 * Displays a table of damage claims with different presentation modes
 *
 * @param {string} variant - Display variant:
 *   - "compact": Simplified view for home page (no filters, limited rows)
 *   - "full": Complete view for claims overview page (filters, pagination, scope selector)
 * @param {number} limit - Maximum number of rows to display (only for compact variant)
 */
export default function ClaimsTable({
  variant = "compact",
  limit = 5,
  onChangeScope = () => {},
  initalScope = "mine",
}) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const claimStates = useClaimStates();
  const [scope, setScope] = useState(initalScope); // "mine" or "all"
  const [activeFilterTab, setActiveFilterTab] = useState("all");
  const [statusCounts, setStatusCounts] = useState(null);

  const isCompact = variant === "compact";
  const isFull = variant === "full";
  const tableKey = isCompact ? "claims_compact" : "claims_full";
  const storageKey = isCompact
    ? "v2_table_claims_compact"
    : "v2_table_claims_full";
  const defaultRowsPerPage = getUserRowsPerPage(user, 5);

  const tablesConfig = useMemo(() => {
    const filters = {};

    if (isFull && scope === "mine") {
      filters.scope = "mine";
    }

    if (isFull && activeFilterTab !== "all") {
      filters.broad_status = activeFilterTab;
    }

    if (isCompact) {
      filters.scope = "mine";
    }

    const extraParams = {
      include: ["objekt"],
      sort: [
        {
          field: "updated_at",
          order: "desc",
        },
      ],
    };

    if (Object.keys(filters).length > 0) {
      extraParams.filters = filters;
    }

    return [
      {
        key: tableKey,
        label: "Schäden",
        route: "/data/schadens",
        pageSize: isCompact ? limit : defaultRowsPerPage,
        storageKey: storageKey,
        extraParams,
      },
    ];
  }, [
    activeFilterTab,
    isCompact,
    isFull,
    limit,
    scope,
    storageKey,
    tableKey,
    defaultRowsPerPage,
  ]);

  const { tableStates, updateTableState } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];
  const totalRows = tableState.totalRows ?? rows.length;
  const pageIndex = tableState.pageIndex ?? 0;
  const pageSize = tableState.pageSize ?? (isCompact ? limit : 10);
  const isLoading = tableState.isLoading ?? false;
  const hasLoaded = tableState.hasLoaded ?? false;

  useEffect(() => {
    if (!isCompact) {
      return;
    }

    // trigger initial load once for compact view
    updateTableState(tableKey, { shouldFetch: true });
  }, [isCompact, tableKey]);

  useEffect(() => {
    if (!isFull) {
      return;
    }

    // Reset pagination and trigger refetch when filters change for full variant
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
  }, [activeFilterTab, isFull, scope, tableKey]);

  useEffect(() => {
    onChangeScope(scope);
  }, [scope]);

  useEffect(() => {
    if (!isFull) {
      return;
    }

    const fetchStatusCounts = async () => {
      const response = await backendGet("/data/schadens/status-counts", {
        label: "Statusübersicht",
        enqueueSnackbar,
        options: {
          params: {
            filter: {
              scope,
            },
            apiVersion: "v2",
          },
        },
      });

      if (response?.data) {
        setStatusCounts(response.data);
      }
    };

    fetchStatusCounts();
  }, [enqueueSnackbar, isFull, scope]);

  // Handle row click
  const handleViewSchaden = (id) => {
    navigate(`/schaden/${id}`);
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "objekt",
        header: "Objekt",
        size: 250,
        Cell: ({ row }) => {
          const objekt = row.original?.objekt;
          const bezeichnung = objekt?.bezeichnung ?? row.original?.bezeichnung;
          const schadenart = row.original?.schaden_art;

          // Main text: use bezeichnung or construct from objekt address
          const mainText =
            bezeichnung ||
            (objekt
              ? `${objekt.strasse || ""} ${objekt.hausnr || ""}`.trim()
              : null) ||
            "Objekt";

          // Subtitle: schadenart or objekt type info
          const subtitle =
            schadenart?.label ||
            schadenart ||
            (objekt?.versicherungsart ? objekt.versicherungsart : null);

          return (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                {mainText}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "schaden_nummer_accredo",
        header: "Schadennummer",
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2">{cell.getValue()}</Typography>
        ),
      },
      {
        accessorKey: "schaden_hoehe",
        header: "Betrag",
        size: 120,
        // workaround: disable sort desc first for this column that is detected as numeric
        // by MRT, since it led to MRT skipping the descending sort
        sortDescFirst: false,
        muiTableHeadCellProps: {
          align: "right",
        },
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            {cell.getValue()
              ? new Intl.NumberFormat("de-DE", {
                  style: "currency",
                  currency: "EUR",
                }).format(cell.getValue())
              : "-"}
          </Typography>
        ),
      },
      {
        accessorKey: "updated_at",
        header: "Aktualisierung",
        size: 120,
        Cell: ({ cell }) => (
          <Typography variant="body2">{getDatum(cell.getValue())}</Typography>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 150,
        filterVariant: "select",
        // Define filter options based on claimStats
        filterSelectOptions: claimStates?.states
          ? claimStates.states.map((item) => ({
              value: item.name,
              label: item.label,
            }))
          : [],
        Cell: ({ cell }) => {
          const status = cell.getValue();
          return (
            <ClaimStatusBadge
              status={status?.value || status}
              label={status?.label || status}
              size="small"
            />
          );
        },
      },
    ],
    [claimStates]
  );

  // Filter tabs for full variant
  const filterTabs = useMemo(() => {
    if (!isFull) {
      return undefined;
    }

    return [
      {
        key: "all",
        label: "Alle",
        count: statusCounts?.all ?? totalRows,
        onClick: setActiveFilterTab,
      },
      {
        key: "saved",
        label: "gespeichert",
        count: statusCounts?.saved ?? 0,
        onClick: setActiveFilterTab,
      },
      {
        key: "processing",
        label: "in Bearbeitung",
        count: statusCounts?.processing ?? 0,
        onClick: setActiveFilterTab,
      },
      {
        key: "closed",
        label: "abgeschlossen",
        count: statusCounts?.closed ?? 0,
        onClick: setActiveFilterTab,
      },
    ];
  }, [isFull, statusCounts, totalRows]);

  // Title component with dropdown for full variant
  const title = useMemo(() => {
    if (isCompact) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: typographyTokens.weight.semibold }}
          >
            Meine zuletzt bearbeiteten Schäden
          </Typography>
          <ViewAllLink label="Alle" to="/schaeden" />
        </Box>
      );
    }

    if (isFull) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography variant="h2">Schäden</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: dimensions["md"],
              }}
            >
              <Typography variant="body1">Auswahl:</Typography>
              <Select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                size="small"
                sx={{
                  px: dimensions["xs"],
                  backgroundColor: colorsFoundation.neutral[100],
                  border: "none",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid " + colorsFoundation.neutral[500],
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "1px solid " + colorsFoundation.key.gold[500],
                  },
                }}
              >
                <MenuItem value="mine">Meine Schäden</MenuItem>
                <MenuItem value="all">Alle Schäden</MenuItem>
              </Select>
            </Box>
          </Box>
        </Box>
      );
    }

    return null;
  }, [isCompact, isFull, scope, navigate]);

  // Pagination change handler
  const handlePaginationChange = (pagination) => {
    const next =
      typeof pagination === "function"
        ? pagination({ pageIndex, pageSize })
        : pagination;

    if (!next) {
      return;
    }

    updateTableState(tableKey, {
      pageIndex: next.pageIndex,
      pageSize: next.pageSize,
    });
  };

  return (
    <DataTable
      columns={columns}
      data={rows}
      isLoading={isLoading}
      hasLoaded={hasLoaded}
      title={title}
      variant="enclosed"
      enableRowActions={false}
      enableSorting
      enableGlobalFilter={isFull}
      enableColumnFilters={isFull}
      enableColumnHiding={isFull}
      topToolbarIcons={isFull}
      enableTopToolbar={isFull}
      enablePagination
      manualPagination
      pageIndex={pageIndex}
      pageSize={pageSize}
      totalRows={totalRows}
      onChangePage={handlePaginationChange}
      storageKey={storageKey}
      onChangeSorting={(sorting) =>
        updateTableState(tableKey, {
          sorting: applySortingWithAccessorKeys(sorting, columns),
        })
      }
      onChangeFilters={(filters) =>
        updateTableState(tableKey, {
          filters,
        })
      }
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, {
          searchword,
        })
      }
      filterTabs={filterTabs}
      activeFilterTab={activeFilterTab}
      onRowClick={(row) => handleViewSchaden(row.id)}
      testId={"claims-table"}
    />
  );
}

ClaimsTable.propTypes = {
  variant: PropTypes.oneOf(["compact", "full"]),
  limit: PropTypes.number,
};
